import { useTranslation } from "react-i18next";
import { SettingRow, ChangeLink, EditPanel } from "./ui";
import { TemplateSlotCard } from "./template-slot-card";
import {
  SlotNames,
  SlotValues,
  describeSlot,
  isSlotComplete,
} from "./template-slot-values";

type TemplateSlotRowProps = {
  label: string;
  saved: SlotValues;
  names: SlotNames;
  /** The values being edited; null while the row is closed. */
  draft: SlotValues | null;
  onEdit: () => void;
  /** Applied on top of the newest draft: the card changes two fields back to back. */
  onDraftChange: (patch: Partial<SlotValues>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
};

/**
 * One generated document in a settings card: what template it uses and where
 * it is filed, with the template fields opened inline under the row.
 */
export function TemplateSlotRow({
  label,
  saved,
  names,
  draft,
  onEdit,
  onDraftChange,
  onSave,
  onCancel,
  saving,
  error,
}: TemplateSlotRowProps) {
  const { t } = useTranslation();

  return (
    <SettingRow
      label={label}
      description={describeSlot(saved, names, t)}
      action={!draft && <ChangeLink onClick={onEdit} />}
    >
      {draft && (
        <EditPanel
          onSave={onSave}
          onCancel={onCancel}
          saving={saving}
          error={error}
        >
          <TemplateSlotCard
            label={label}
            source={draft.source}
            templateId={draft.templateId}
            seriesId={draft.seriesId}
            fileTypeId={draft.fileTypeId}
            categoryId={draft.categoryId}
            configured={isSlotComplete(draft)}
            onSourceChange={(source) => onDraftChange({ source })}
            onTemplateChange={(templateId) => onDraftChange({ templateId })}
            onSeriesChange={(seriesId) => onDraftChange({ seriesId })}
            onFileTypeChange={(fileTypeId) => onDraftChange({ fileTypeId })}
            onCategoryChange={(categoryId) => onDraftChange({ categoryId })}
          />
        </EditPanel>
      )}
    </SettingRow>
  );
}
